import styles from './ModifierSelector.module.scss';

import clsx from 'clsx';
import { MENU_ITEM_MODAL_TEST_ID } from '../MenuItemModalTestIds';
import React, { ChangeEvent, FC, FormEventHandler, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnumInput } from 'components/EnumInput';
import { Modifier, ModifierGroup as ModifierGroupType } from 'olo-service';
import Typography from 'components/Typography/Typography';

type FieldsetProps = React.DetailedHTMLProps<React.FieldsetHTMLAttributes<HTMLFieldSetElement>, HTMLFieldSetElement>;

type ModifierSelectorProps = Omit<FieldsetProps, 'onChange'> & {
  group: ModifierGroupType;
  value?: ReadonlyArray<Modifier>;
  onChange: (group: ModifierGroupType, modifiers: Modifier[]) => void;
  children?: never;
}

export const ModifierSelector: FC<ModifierSelectorProps> = ({
  group, onChange, value = [], className, disabled: fieldSetDisabled, ...props
}) => {
  const { modifiers, allModifiersUnavailable } = useMemo(() => {
    const groupModifiers: Modifier[] = group.modifiers ?? [];
    const groupUnavailableModifiers: Modifier[] = groupModifiers.filter(item => !item.available);
    const groupAllModifiersUnavailable = groupModifiers.length === groupUnavailableModifiers.length;

    return { modifiers: groupModifiers, allModifiersUnavailable: groupAllModifiersUnavailable };
  }, [group.modifiers]);
  const { t } = useTranslation();

  const [hasError, setHasError] = useState(false);

  const { minRequired, maxAllowed } = useMemo(() => {
    const calculatedMaxAllowed = Math.min(group.modifiers.length, Math.max(0, group.maxAllowed ?? group.modifiers.length));
    const calculatedMinRequired = Math.min(calculatedMaxAllowed, Math.max(0, group.minRequired ?? 0));

    return { minRequired: calculatedMinRequired, maxAllowed: calculatedMaxAllowed };
  }, [group.maxAllowed, group.minRequired, group.modifiers.length]);

  const isRadio = minRequired === 1 && maxAllowed === 1;
  const isBetweenMinMax = minRequired <= value.length && value.length <= maxAllowed;

  // Auto-select the first item if isRadio
  useEffect(() => {
    // Only pre-select for radio buttons.
    // If there are no modifiers or at least one is already selected then we don't need to check.
    if (!isRadio || !group.modifiers.length || value.length || allModifiersUnavailable) return;

    const firstAvailableModifier = modifiers.findIndex(modifier => modifier.available);
    onChange(group, [modifiers[firstAvailableModifier]]);
  }, [isRadio, group, value, onChange, modifiers, allModifiersUnavailable]);

  // Verify that the group matches the selected values
  useEffect(() => {
    const filteredValue = value.filter(selectedMod => modifiers.find(mod => mod.id === selectedMod.id));

    // If the lengths are the same then all were valid.
    if (filteredValue.length === value.length) return;

    if (process.env.NODE_ENV === 'development') {
      throw new Error(`One or more of the selected ${group.name} modifiers do not exist in the modifier group ${group.id}.`);
    }

    // Fail gracefully
    onChange(group, filteredValue);
  }, [value, group, onChange, modifiers]);

  const handleChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>, modifier: Modifier) => {
    setHasError(false); // If the user interacts with the inputs then hide the errors for now

    if (isRadio && target.checked) {
      onChange(group, [modifier]);

      return;
    }

    onChange(group, target.checked ? [...value, modifier] : value.filter(mod => mod.id !== modifier.id));
  }, [group, isRadio, onChange, value]);

  const helperText = useMemo(() => {
    if (isRadio && !allModifiersUnavailable) return;

    if (isRadio && allModifiersUnavailable) return t('numRequired', { numRequired: minRequired });

    if (minRequired > 0 && maxAllowed < modifiers.length) {
      return t('numRequiredAndMaximum', { numRequired: minRequired, numMaximum: maxAllowed });
    }

    if (minRequired > 0) return t('numRequired', { numRequired: minRequired });

    if (maxAllowed < modifiers.length) return t('numMaximum', { numMaximum: maxAllowed });
  }, [isRadio, maxAllowed, minRequired, modifiers, allModifiersUnavailable, t]);

  const handleInvalid: FormEventHandler<HTMLElement> = useCallback(() => setHasError(true), []);

  // If there are no modifiers OR none are allowed to be selected
  if (!modifiers.length || maxAllowed === 0) return null;

  return (
    <fieldset role="group" className={clsx(styles.modifierGroup, className)} disabled={fieldSetDisabled} {...props}>
      <div className={styles.legend}>
        <legend>{group.name}</legend>
        {helperText && <p className={clsx({ [styles.error]: hasError })}>{helperText}</p>}
      </div>
      {modifiers.map((modifier, index) => {
        const checked = !!value.find(v => v.id === modifier.id);
        const showPrice = Boolean(modifier.price && modifier.available);
        // If the fieldset is disabled OR If the user is already at the maximum then disable the unchecked options
        const disabled = (!checked && !isRadio && value.length >= maxAllowed) || fieldSetDisabled || !modifier.available;

        return (
          <div key={modifier.id} className={styles.itemRow}>
            <EnumInput
              type={isRadio ? 'radio' : 'checkbox'}
              data-testid={isRadio ? MENU_ITEM_MODAL_TEST_ID.radioButton : MENU_ITEM_MODAL_TEST_ID.checkbox}
              className={clsx(styles.input, disabled && styles.disabled)}
              name={group.name}
              label={modifier.name}
              value={modifier.name}
              required={!isRadio && value.length < minRequired}
              checked={checked}
              disabled={disabled}
              customError={!isBetweenMinMax}
              onChange={e => handleChange(e, modifier)}
              onInvalid={handleInvalid}
              inputProps={{
                'aria-describedby': showPrice ? `${modifier.id}-price` : undefined
              }}
            >
              {showPrice && (
                <p id={`${modifier.id}-price`} className={styles.price}>
                  +{t('money', { amount: modifier.price })}
                </p>
              )}
              {!modifier.available &&
                <Typography.Body color={'gray'} data-testid={MENU_ITEM_MODAL_TEST_ID.unavailable}>{t('cart.unavailable')}</Typography.Body>
              }
            </EnumInput>
          </div>
        );
      })}
    </fieldset>
  );
};
